import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

/**
 * Atajos de teclado globales tipo Vim/Linear/GitHub.
 *
 * Secuencias de 2 teclas (prefijo + letra) con timeout de 1.5s.
 *
 *   g f → Flujo de Caja
 *   g n → CxP Nóminas
 *   g c → Cuentas por Cobrar
 *   g o → Listado OC
 *   g r → Requisiciones
 *   g d → Dashboard
 *   g a → Asistencia Mensual
 *   g v → Mis VB Supervisor
 *   g s → Stock Insumos
 *   g i → Bandeja Aprobaciones
 *   g m → Manual de Uso
 *   g k → Reportes Programados
 *   c o → Crear OC
 *   c r → Crear Requisición
 *   ?   → Mostrar ayuda con todos los atajos
 *
 * No se activa cuando el foco está en un input/textarea/contenteditable
 * o si hay teclas modificadoras (Ctrl/Cmd/Alt) presionadas.
 */
export const SHORTCUTS = [
  // g + X → Ir a
  { keys: ['g', 'f'], label: 'Ir a Flujo de Caja',            route: '/contabilidad/flujo-caja' },
  { keys: ['g', 'n'], label: 'Ir a CxP Nóminas',              route: '/contabilidad/cxp-nominas' },
  { keys: ['g', 'c'], label: 'Ir a Cuentas por Cobrar',       route: '/cuentas-por-cobrar' },
  { keys: ['g', 'o'], label: 'Ir a Listado de OC',            route: '/compras/ordenes-compra' },
  { keys: ['g', 'r'], label: 'Ir a Requisiciones',            route: '/compras/requisiciones' },
  { keys: ['g', 'd'], label: 'Ir a Dashboard',                route: '/dashboard' },
  { keys: ['g', 'a'], label: 'Ir a Asistencia Mensual',       route: '/rrhh/asistencia-mensual' },
  { keys: ['g', 'v'], label: 'Ir a Mis VB Supervisor',        route: '/rrhh/mis-vb' },
  { keys: ['g', 'm'], label: 'Ir a Mis Partes',                route: '/rrhh/mis-partes' },
  { keys: ['g', 's'], label: 'Ir a Stock Insumos',            route: '/bodega/stock-insumos' },
  { keys: ['g', 'i'], label: 'Ir a Bandeja Aprobaciones',     route: '/aprobaciones' },
  { keys: ['g', 'm'], label: 'Ir al Manual de Uso',           route: '/manual' },
  { keys: ['g', 'k'], label: 'Ir a Reportes Programados',     route: '/configuracion/reportes-programados' },
  // c + X → Crear
  { keys: ['c', 'o'], label: 'Crear nueva OC',                route: '/compras/generar-oc' },
  { keys: ['c', 'r'], label: 'Crear nueva Requisición',       route: '/compras/requisiciones?action=new' },
];

const PREFIXES = new Set(SHORTCUTS.map((s) => s.keys[0]));
const TIMEOUT_MS = 1500;

function shouldIgnoreTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  // cmdk uses a hidden input; ignore any role=combobox
  if (target.getAttribute && target.getAttribute('role') === 'combobox') return true;
  // El foco puede estar dentro de un desplegable, menú, listbox o diálogo abierto
  // (Radix mueve el foco a estos contenedores, no a un <input>). Ahí las teclas
  // son para filtrar/navegar la lista, no para disparar atajos globales.
  if (typeof target.closest === 'function') {
    if (
      target.closest(
        'input, textarea, select, [contenteditable="true"], ' +
        '[role="combobox"], [role="listbox"], [role="option"], ' +
        '[role="menu"], [role="menuitem"], [role="dialog"], [role="alertdialog"]',
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Detecta si hay algún overlay abierto en el documento (desplegable/Select,
 * menú, popover, modal/diálogo, buscador cmdk). Mientras algo esté abierto los
 * atajos globales quedan inhibidos: así escribir para buscar dentro de una lista
 * (ej. "Banco / Cuenta de origen") solo filtra y nunca navega ni cierra la pantalla.
 *
 * Radix renderiza el contenido en el DOM solo cuando está abierto, por lo que la
 * presencia de estos selectores equivale a "hay algo abierto".
 */
function hasBlockingOverlay() {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector(
    '[data-radix-popper-content-wrapper],' +
    '[role="dialog"][data-state="open"],' +
    '[role="alertdialog"][data-state="open"],' +
    '[role="menu"][data-state="open"],' +
    '[role="listbox"],' +
    '[cmdk-root]',
  );
}

/**
 * Hook que registra los atajos globales y mantiene exposición del estado
 * "prefijo pendiente" para que un indicador visual pueda mostrarlo.
 */
export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const [pendingPrefix, setPendingPrefix] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  const lookup = useMemo(() => {
    const m = new Map();
    for (const s of SHORTCUTS) m.set(`${s.keys[0]}${s.keys[1]}`, s);
    return m;
  }, []);

  useEffect(() => {
    const clearPending = () => {
      pendingRef.current = null;
      setPendingPrefix(null);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handler = (e) => {
      // Ignore inputs / modifiers
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (shouldIgnoreTarget(e.target)) return;
      // Inhibir atajos mientras haya un desplegable, menú, modal o buscador abierto.
      if (hasBlockingOverlay()) {
        clearPending();
        return;
      }
      const key = e.key.toLowerCase();

      // "?" → mostrar/ocultar ayuda (Shift+/ en US, "?" directo en algunos layouts)
      if (e.key === '?' || (e.shiftKey && key === '/')) {
        e.preventDefault();
        setShowHelp((v) => !v);
        clearPending();
        return;
      }
      if (key === 'escape') {
        if (pendingRef.current || showHelp) {
          clearPending();
          setShowHelp(false);
        }
        return;
      }

      if (pendingRef.current) {
        // Buscar combinación
        const combo = `${pendingRef.current}${key}`;
        const sc = lookup.get(combo);
        clearPending();
        if (sc) {
          e.preventDefault();
          navigate(sc.route);
          toast({ title: `⌨️ ${pendingRef.current.toUpperCase()} + ${key.toUpperCase()}`, description: sc.label });
        }
        return;
      }

      // ¿Es un prefijo válido?
      if (PREFIXES.has(key)) {
        e.preventDefault();
        pendingRef.current = key;
        setPendingPrefix(key);
        timerRef.current = setTimeout(() => clearPending(), TIMEOUT_MS);
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate, lookup, showHelp]);

  return { pendingPrefix, showHelp, setShowHelp };
}
